import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authApi, customerApi } from '../api';

interface User {
  id: string;
  phone: string;
  name: string;
  role: string;
  tenantId: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  currentCustomer: string | null;
  login: (phone: string, password: string) => Promise<void>;
  register: (data: { phone: string; code?: string; password: string; name?: string }) => Promise<void>;
  logout: () => void;
  enterCustomer: (id: string) => Promise<void>;
  backToOrg: () => void;
}

const AuthContext = createContext<AuthState>({} as AuthState);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('tax_token'));
  const [currentCustomer, setCurrentCustomer] = useState<string | null>(() => localStorage.getItem('tax_current_customer'));
  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem('tax_user');
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      authApi
        .me()
        .then((res: any) => {
          setUser(res.user);
          localStorage.setItem('tax_user', JSON.stringify(res.user));
        })
        .catch(() => {
          setToken(null);
          setUser(null);
          localStorage.removeItem('tax_token');
          localStorage.removeItem('tax_user');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const persist = (t: string, customer: string | null) => {
    localStorage.setItem('tax_token', t);
    if (customer) localStorage.setItem('tax_current_customer', customer);
    else localStorage.removeItem('tax_current_customer');
    setToken(t);
    setCurrentCustomer(customer);
  };

  const login = async (phone: string, password: string) => {
    const res: any = await authApi.login({ phone, password });
    localStorage.setItem('tax_org_token', res.token);
    persist(res.token, null);
    setUser(res.user);
    localStorage.setItem('tax_user', JSON.stringify(res.user));
  };

  const register = async (data: { phone: string; code?: string; password: string; name?: string }) => {
    const res: any = await authApi.register(data);
    localStorage.setItem('tax_org_token', res.token);
    persist(res.token, null);
    setUser(res.user);
    localStorage.setItem('tax_user', JSON.stringify(res.user));
  };

  const enterCustomer = async (id: string) => {
    const org = localStorage.getItem('tax_org_token') || token;
    if (org) localStorage.setItem('tax_org_token', org);
    const res: any = await customerApi.enter(id);
    persist(res.token, res.customerName);
  };

  const backToOrg = async () => {
    try {
      const res: any = await authApi.backToOrg();
      persist(res.token, null);
    } catch {
      // 已失效则重新登录
      localStorage.removeItem('tax_token');
      localStorage.removeItem('tax_current_customer');
      setToken(null);
      setCurrentCustomer(null);
      location.href = '/login';
    }
  };

  const logout = () => {
    authApi.logout().catch(() => {});
    localStorage.removeItem('tax_token');
    localStorage.removeItem('tax_org_token');
    localStorage.removeItem('tax_current_customer');
    localStorage.removeItem('tax_user');
    setToken(null);
    setUser(null);
    setCurrentCustomer(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, currentCustomer, login, register, logout, enterCustomer, backToOrg }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
